/**
 * A bundle's pieces planned as an update of a book already on the device (L11's update, C6; part 24d log, principles 2–4,
 * 7, tables U, F and N). A {@link BundleUpdate} is part 24c's {@link BundleRestore} over a hidden staging book: what the
 * book lacks is created there under its own ID (table N), and what it holds is kept, never created twice; the chapters both
 * have are kept aside with the bundle's text, fields and history hashes for the review (table U), the notes both hold with
 * other words as offers (F5; part 19e, MB8, MB9). {@link chapterUpdate} decides each such chapter from the histories
 * (core's `mergeSources` merges one three ways, in a worker). Pure: the IDs, the time, SHA-256 and what the book holds are
 * given. A share both hold is kept aside too (part 38b, table BU), for {@link mergeShares} to merge with the record as
 * stored when the review is made. A note's share part (part 38e, BV4) comes with whichever version of the note is kept.
 */
import { toHex } from '@cockatrice/core';
import { bookNoteNote, chapterNotesNote, commentId as commentIdOf, commentNote } from '@cockatrice/storage';
import type { ChapterEntry, ImportOp, InviteRecord, NoteRecord, RoleKey, ShareRecord } from '@cockatrice/storage';
import { shareRecordOf } from './manifest.ts';
import { BundleRestore, sentInvites } from './restore.ts';
import type { RecoveredLabels, RestoreCounts } from './restore.ts';
import type { BookNotePiece, ChapterPiece, CommentPiece, CopyPiece, ImagePiece, NotePiece, RefusedPiece, ShareLinkPiece, SharePiece, SnapshotPiece } from './pieces.ts';

/** What the book holds when the update begins (principle 3): every ID by store, readable or refused, and what it reads as. */
export interface UpdateHoldings {
  /** The book's chapter entries it can read, by ID, live and in the trash. */
  readonly entries: ReadonlyMap<string, ChapterEntry>;
  /** The keys of the book's entries its reader refuses. */
  readonly refusedEntries: ReadonlySet<string>;
  readonly snapshots: ReadonlySet<string>;
  readonly copies: ReadonlySet<string>;
  /** The book's notes it can read, tombstones too, by ID (part 19e). */
  readonly notes: ReadonlyMap<string, NoteRecord>;
  /** The keys of the book's notes its reader refuses. */
  readonly refusedNotes: ReadonlySet<string>;
  /** The book's reading links, readable or refused, by ID (part 38d). */
  readonly shareLinks: ReadonlySet<string>;
  /** The book's shares it can read, by ID (part 38b, BU). */
  readonly shares: ReadonlyMap<string, ShareRecord>;
  /** The keys of the book's shares their reader refuses. */
  readonly refusedShares: ReadonlySet<string>;
  readonly images: ReadonlySet<string>;
  /** The bytes the book's images hold now: where the cap starts (N9). */
  readonly imageBytes: number;
}

/** What an update needs; everything with an effect is given. */
export interface UpdateOptions {
  /** The book updated (the bundle's own). */
  readonly bookId: string;
  /** The staging book the update's records are created under (principle 2), a fresh ID. */
  readonly staging: string;
  /** Table H: the update's own snapshot owner, a fresh ID (decision 29). */
  readonly owner: string;
  /** When the update runs, in milliseconds since the Unix epoch. */
  readonly now: number;
  /** A fresh ID (32 lowercase hex digits). */
  readonly newId: () => string;
  /** SHA-256: the histories' hashes (principle 4) and remade Word note IDs (N8). */
  readonly digest: (bytes: Uint8Array) => Promise<Uint8Array>;
  /** N9: the most bytes the book's images may hold (`BOOK_IMAGE_CAP`). */
  readonly cap: number;
  /** Table B′, H′2: the time zone offset at `now`, in minutes east of UTC. */
  readonly zone: number;
  readonly holdings: UpdateHoldings;
  /** N2, N3: whether any record, of any book, readable or refused, holds chapter ID `id` (an entry or a chapter text). */
  readonly chapterHeld: (id: string) => Promise<boolean>;
}

/** Table U: a chapter the bundle and the book both have, as the bundle has it. */
export interface MatchedChapter {
  readonly id: string;
  /** N1: the ID its staged records name until Apply hands them to it. */
  readonly staging: string;
  /** B: the bundle's text, or null when its store couldn't read it (U1). */
  readonly text: string | null;
  readonly title: string;
  readonly synopsis: string;
  /** Hb: the SHA-256 of the bundle's text and of each of its snapshots' texts of the chapter, in hex. */
  readonly hashes: ReadonlySet<string>;
  /** U8, F2: whether the book's entry is one its reader refuses (nothing of the chapter is then decided). */
  readonly refused: boolean;
}

/** A chapter the update adds (N2, N3), for the review. */
export interface AddedChapter {
  readonly id: string;
  readonly title: string;
  readonly trashed: boolean;
}

/**
 * F5, MB8, MB9: a note of the writer's both hold standing under one ID with another title, group or text: the book's, and
 * the bundle's words in its place.
 */
export interface NoteOffer {
  readonly before: NoteRecord;
  readonly after: NoteRecord;
}

/** What the update staged, for the writer (the review and the outcome). */
export interface UpdateCounts extends RestoreCounts {
  /** Records the book already held, kept as they are (principle 3). */
  readonly held: number;
}

/** The SHA-256 of `text`'s UTF-8, in lowercase hex (principle 4). */
export async function textHash(digest: (bytes: Uint8Array) => Promise<Uint8Array>, text: string): Promise<string> {
  return toHex(await digest(new TextEncoder().encode(text)));
}

/** A snapshot of this device's history of a chapter (principle 4): its ID, its place, and its text's hash. */
export interface HistoryItem {
  readonly id: string;
  readonly createdAt: number;
  readonly seq: number;
  /** The SHA-256 of its text, in hex ({@link textHash}). */
  readonly sha256: string;
}

/**
 * What one chapter both have with differing texts gets (table U): nothing (U4), the bundle's text (U3), a merge on a base
 * (U5: the snapshot of this device's history it is), or two-way (U6, U7).
 */
export type ChapterUpdate<T extends HistoryItem = HistoryItem> =
  | { readonly kind: 'take' }
  | { readonly kind: 'newer-here' }
  | { readonly kind: 'merge'; readonly base: T }
  | { readonly kind: 'two-way' };

/** What {@link chapterUpdate} compares: the bundle's text's hash and history, and this device's (items of `T`). */
export interface ChapterHistories<T extends HistoryItem = HistoryItem> {
  /** B's hash. */
  readonly bundleHash: string;
  /** Hb: the bundle's text's and its snapshots' hashes. */
  readonly bundleHashes: ReadonlySet<string>;
  /** C's hash. */
  readonly currentHash: string;
  /** This device's snapshots of the chapter (the update's own staged ones not among them). */
  readonly history: readonly T[];
}

/**
 * Table U, U3–U7 (principle 4): what a chapter both have gets when B differs from C (U2, the same texts, is the caller's,
 * decided before the histories are read). U3: C is in the bundle's history and B isn't in this device's (only the bundle
 * moved on). U4: the other way round. U7: both (one side went back to an earlier text): two-way. Otherwise the base is this
 * device's newest snapshot (by time, then `seq`) whose text the bundle's history holds (U5), and with none, two-way (U6).
 */
export function chapterUpdate<T extends HistoryItem>(input: ChapterHistories<T>): ChapterUpdate<T> {
  const bundleMovedOn = input.bundleHashes.has(input.currentHash);
  // B differs from C, so B in this device's history is B in a snapshot's text.
  const hereMovedOn = input.history.some((item) => item.sha256 === input.bundleHash);
  if (bundleMovedOn !== hereMovedOn) return bundleMovedOn ? { kind: 'take' } : { kind: 'newer-here' };
  if (bundleMovedOn) return { kind: 'two-way' };
  let base: T | null = null;
  for (const item of input.history) {
    if (input.bundleHashes.has(item.sha256) && (base === null || item.createdAt > base.createdAt || (item.createdAt === base.createdAt && item.seq > base.seq))) base = item;
  }
  return base === null ? { kind: 'two-way' } : { kind: 'merge', base };
}

/** BU: whether two invites hold the same value in every field. */
function sameInvite(a: InviteRecord, b: InviteRecord): boolean {
  const chapters = a.chapters === null || b.chapters === null ? a.chapters === b.chapters : a.chapters.from === b.chapters.from && a.chapters.to === b.chapters.to;
  return (
    chapters &&
    a.id === b.id &&
    a.email === b.email &&
    a.role === b.role &&
    a.notes === b.notes &&
    a.export === b.export &&
    a.endsAt === b.endsAt &&
    a.claim === b.claim &&
    a.key === b.key &&
    a.createdAt === b.createdAt &&
    a.changedAt === b.changedAt &&
    a.sent === b.sent &&
    a.removedAt === b.removedAt
  );
}

/** The earlier of two times, either of which may be none. */
function earlier(a: number | null, b: number | null): number | null {
  return a === null ? b : b === null ? a : Math.min(a, b);
}

/**
 * BU: an invite both records hold: the same, kept as it is; else the one changed later (ties: `held`), with the earlier
 * removal of either side, and unsent (so the device sends its settings again).
 */
function mergeInvite(held: InviteRecord, bundled: InviteRecord): InviteRecord {
  if (sameInvite(held, bundled)) return held;
  const kept = bundled.changedAt > held.changedAt ? bundled : held;
  return { ...kept, sent: null, removedAt: earlier(held.removedAt, bundled.removedAt) };
}

/** BU: an end from either side; with both, the earlier, `gone` if either says so. */
function mergeEnd(held: ShareRecord['ended'], bundled: ShareRecord['ended']): ShareRecord['ended'] {
  if (held === null) return bundled;
  if (bundled === null) return held;
  return { at: Math.min(held.at, bundled.at), why: held.why === 'gone' || bundled.why === 'gone' ? 'gone' : 'ended' };
}

/**
 * BV4 (part 38e): the role keys of two records of a share, by group and epoch: one only either side holds kept, the held
 * record's first; one both hold is this device's (its notes are under it), with the holders of both when the keys are the
 * same, else this device's alone (the bundle's holders were sent the other device's key, not this one: RK14); deep-fix r2
 * (table RJ): the withheld of both, when the keys are the same.
 */
function mergeRoleKeys(held: readonly RoleKey[], bundled: readonly RoleKey[]): RoleKey[] {
  const place = (key: RoleKey): string => `${key.group}:${String(key.epoch)}`;
  const theirs = new Map(bundled.map((key) => [place(key), key]));
  const ours = new Set(held.map(place));
  return [
    ...held.map((key) => {
      const other = theirs.get(place(key));
      if (other?.key !== key.key) return key;
      const holders = new Set(key.holders);
      // Deep-fix r2, table RJ: withheld from either side stays withheld (the stricter).
      const withheld = new Set(key.withheld);
      // Deep-fix r4, table KP: pending from either side stays pending (a leaver if it goes).
      const pending = new Set(key.pending);
      return {
        ...key,
        holders: [...key.holders, ...other.holders.filter((holder) => !holders.has(holder))],
        withheld: [...key.withheld, ...other.withheld.filter((each) => !withheld.has(each))],
        pending: [...key.pending, ...other.pending.filter((each) => !pending.has(each))],
      };
    }),
    ...bundled.filter((key) => !ours.has(place(key))),
  ];
}

/**
 * Table BU (part 38b, L11's update): the share this device holds (`held`) and a bundle's of the same ID, merged: the
 * invites by ID, one only either side holds kept, one both hold as {@link mergeInvite}; the held record's pushed items
 * (what this device pushed), made if either was, the earlier creation, the role keys as {@link mergeRoleKeys} (BV4), an
 * end from either side ({@link mergeEnd}). Its ID, book and secret are the held record's; with another secret (a forged
 * or broken bundle) the held record is kept unchanged. Idempotent: merging the bundle's again changes nothing, and no
 * invite or role key either side holds is dropped.
 */
export function mergeShares(held: ShareRecord, bundled: ShareRecord): ShareRecord {
  if (held.secret !== bundled.secret) return held;
  const theirs = new Map(bundled.invites.map((invite) => [invite.id, invite]));
  const ours = new Set(held.invites.map((invite) => invite.id));
  const invites = [
    ...held.invites.map((invite) => {
      const other = theirs.get(invite.id);
      return other === undefined ? invite : mergeInvite(invite, other);
    }),
    // Fix-next 85, BO8: one only the bundle holds, never sent, is the bundle's writer's, not this device's to send.
    ...sentInvites(bundled.invites).filter((invite) => !ours.has(invite.id)),
  ];
  return {
    id: held.id,
    bookId: held.bookId,
    secret: held.secret,
    createdAt: Math.min(held.createdAt, bundled.createdAt),
    made: held.made || bundled.made,
    invites,
    pushed: held.pushed,
    roleKeys: mergeRoleKeys(held.roleKeys, bundled.roleKeys),
    ended: mergeEnd(held.ended, bundled.ended),
  };
}

/** Tables N and U over one bundle's pieces, staged under the staging book. */
export class BundleUpdate extends BundleRestore {
  readonly #update: UpdateOptions;
  /** Table U: the chapters both have, by ID, their hashes growing as the bundle's snapshots of them are met. */
  readonly #matched = new Map<string, MatchedChapter & { readonly hashes: Set<string> }>();
  readonly #added: AddedChapter[] = [];
  readonly #offers: NoteOffer[] = [];
  readonly #shares: ShareRecord[] = [];
  #held = 0;

  constructor(options: UpdateOptions) {
    super({ bookId: options.staging, mode: 'restore', owner: options.owner, now: options.now, newId: options.newId, digest: options.digest, cap: options.cap, zone: options.zone });
    this.#update = options;
    // N9: the book's own images count under the cap first.
    this.used = options.holdings.imageBytes;
  }

  /** Table U: the chapters both have, in the bundle's order. */
  matched(): readonly MatchedChapter[] {
    return [...this.#matched.values()];
  }

  /** N2, N3: the chapters the update adds, in the bundle's order. */
  added(): readonly AddedChapter[] {
    return [...this.#added];
  }

  /** F5: the notes offered. */
  noteOffers(): readonly NoteOffer[] {
    return [...this.#offers];
  }

  /** BU: the bundle's shares the book holds readable, as the book's records, for {@link mergeShares} at the review. */
  shareMerges(): readonly ShareRecord[] {
    return [...this.#shares];
  }

  /** N1: each matched chapter's staging ID and its own, for the hand-over (`adopt-import`). */
  stagingIds(): readonly (readonly [string, string])[] {
    return this.matched().map((chapter) => [chapter.staging, chapter.id]);
  }

  /**
   * N12, 24c's P23: the recovered chapters' records under the staging book, after the later of `after` (the book's last
   * chapter with a valid position, null for none) and the update's new chapters' last.
   */
  override finish(labels: RecoveredLabels, pictures: readonly string[], after: string | null = null): ImportOp[] {
    // Valid positions order by their code units (decision 62).
    return super.finish(labels, pictures, after === null || (this.last !== null && this.last > after) ? this.last : after);
  }

  /**
   * Table B′ (deep-fix r1), H′4 (deep-fix r3): the bundle's text of the matched chapter staged as `staging` (its
   * {@link MatchedChapter}'s), kept as the update's own hand-over snapshot (H′2's, pinned `bundle`) under that staging ID, so
   * Apply's write adopts it with the rest: for a chapter whose text Apply doesn't write whole and this device's history
   * doesn't hold.
   */
  keepText(staging: string, text: string): ImportOp[] {
    return this.handover(staging, text);
  }

  /**
   * Table G′ (deep-fix r2): the bundle's text of chapter `chapterId`, which was deleted forever here since the update began,
   * kept as a `bundle-update` conflict copy of it (made now, under the staging book), which shows as a copy whose chapter is
   * gone (part 08's X4).
   */
  keepCopy(chapterId: string, text: string): ImportOp[] {
    const { now, newId } = this.#update;
    return [{ kind: 'add', store: 'conflictCopies', value: { id: newId(), chapterId, bookId: this.options.bookId, text, createdAt: now, origin: 'bundle-update' } }];
  }

  /** What the update staged, and how many of the bundle's records the book already held. */
  override counts(): UpdateCounts {
    return { ...super.counts(), held: this.#held };
  }

  /** N1–N3: a chapter both have kept aside (table U); one the book lacks staged under its ID, or a fresh one when that is held. */
  protected override async chapter(piece: ChapterPiece): Promise<ImportOp[]> {
    const { holdings, digest, newId, chapterHeld } = this.#update;
    const refused = holdings.refusedEntries.has(piece.id);
    if (holdings.entries.has(piece.id) || refused) {
      const staging = newId();
      this.chapters.set(piece.id, staging);
      const hashes = new Set<string>(piece.text === null ? [] : [await textHash(digest, piece.text)]);
      this.#matched.set(piece.id, { id: piece.id, staging, text: piece.text, title: piece.title, synopsis: piece.synopsis, hashes, refused });
      // MB8: a version 1 to 4 bundle's notes of the chapter against its note in the book (the chapter's ID, MG2).
      const note = chapterNotesNote(piece.id, { id: staging, bookId: this.options.bookId }, piece.notes, this.#update.now);
      return note === null ? [] : this.#settle(note, 'notes');
    }
    // N3 (24c's CR3): an ID another book or a refused record holds is never this chapter's.
    if (await chapterHeld(piece.id)) this.chapters.set(piece.id, newId());
    const ops = await super.chapter(piece);
    this.#added.push({ id: this.chapterId(piece.id, true), title: piece.title, trashed: piece.trashed !== null });
    return ops;
  }

  /** N5: a conflict copy the book holds is kept; another staged. */
  protected override copy(piece: CopyPiece): ImportOp | null {
    return this.#keeps(this.#update.holdings.copies, piece.id) ? null : super.copy(piece);
  }

  /** N6, principle 4: a snapshot's text counts in its chapter's history; one the book holds is kept, another staged. */
  protected override async snapshot(piece: SnapshotPiece): Promise<ImportOp[]> {
    const matched = this.#matched.get(piece.chapterId);
    if (matched !== undefined && piece.text !== null) matched.hashes.add(await textHash(this.#update.digest, piece.text));
    return this.#keeps(this.#update.holdings.snapshots, piece.id) ? [] : super.snapshot(piece);
  }

  /** N9: an image the book holds by ID is kept; another as 24c's P6–P8. */
  protected override image(piece: ImagePiece, found: Uint8Array[]): ImportOp[] {
    return piece.state === 'kept' && this.#keeps(this.#update.holdings.images, piece.id) ? [] : super.image(piece, found);
  }

  /** N7, F5, MB3, MB9: a book note, as a Notebook note: kept when the book holds it, offered when its words differ; else staged. */
  protected override bookNote(piece: BookNotePiece): ImportOp | null {
    const { kind, name, text, createdAt } = piece;
    const [op] = this.#settle(bookNoteNote(piece.id, { id: piece.id, bookId: this.options.bookId, kind, name, text, createdAt }), 'notes');
    return op ?? null;
  }

  /**
   * N8 (C6, 24c's CR5), MB4: a comment, as a Word note: kept when the book holds its ID; else staged, its ID kept while its
   * chapter keeps the bundle's ID, else made again from the book, that chapter and its key (part 23c's DD).
   */
  protected override async comment(piece: CommentPiece): Promise<ImportOp | null> {
    const staged = this.chapterId(piece.chapterId);
    const id = await this.#wordId(piece.id, piece.chapterId, piece.sourceKey);
    const { sourceKey, origin, author, text, createdAt, storedAt, range, quote, before, after } = piece;
    const [op] = this.#settle(commentNote(id, { id, bookId: this.options.bookId, chapterId: staged, sourceKey, origin, author, text, createdAt, storedAt, range, quote, before, after }), 'comments');
    return op ?? null;
  }

  /**
   * V5-1, MB9 (part 19e): a note record: kept when the book holds it, offered when its words differ; else staged. BV4 (part
   * 38e): its share part with it, the book's when kept (an offer takes the bundle's words alone), the bundle's when staged.
   */
  protected override async note(piece: NotePiece): Promise<ImportOp | null> {
    const staged = piece.chapterId === null ? null : this.chapterId(piece.chapterId);
    if (piece.sourceKey !== null && piece.chapterId !== null) this.notes.set(piece.id, await this.#wordId(piece.id, piece.chapterId, piece.sourceKey));
    const [op] = this.#settle(await this.noteRecord(piece, staged), piece.deletedAt === null ? 'notes' : null);
    return op ?? null;
  }

  /** N8: a Word note's ID in the book: the bundle's while its chapter keeps the bundle's ID, else made again (part 23c's DD). */
  async #wordId(id: string, bundleChapter: string, sourceKey: string): Promise<string> {
    const { bookId, digest } = this.#update;
    const chapterId = this.#matched.get(bundleChapter)?.id ?? this.chapterId(bundleChapter);
    return chapterId === bundleChapter ? id : commentIdOf(digest, bookId, chapterId, sourceKey);
  }

  /**
   * Principle 3, F5, MB8, MB9: `note` (staged, counted in `count`) when the book holds no note of its ID, readable or
   * refused; else kept as the book has it, and offered when both stand, both are the writer's, and its title, group or
   * text differ.
   */
  #settle(note: NoteRecord, count: 'notes' | 'comments' | null): ImportOp[] {
    const { holdings } = this.#update;
    const before = holdings.notes.get(note.id);
    if (before === undefined && !holdings.refusedNotes.has(note.id)) {
      if (count !== null) this.tally[count] += 1;
      return [{ kind: 'add', store: 'notes', value: note }];
    }
    this.#held += 1;
    const offered = before?.deletedAt === null && note.deletedAt === null && before.origin === 'writer' && note.origin === 'writer';
    if (offered && (before.title !== note.title || before.group !== note.group || before.text !== note.text)) {
      this.#offers.push({ before, after: { ...before, title: note.title, group: note.group, text: note.text } });
    }
    return [];
  }

  /** V6-5 (part 38d, L11: by their IDs): a reading link the book holds is kept as the book has it; else staged. */
  protected override shareLink(piece: ShareLinkPiece): ImportOp[] {
    return this.#keeps(this.#update.holdings.shareLinks, piece.id) ? [] : super.shareLink(piece);
  }

  /**
   * BU (part 38b, L11's update): a share the book holds readable is kept aside to merge ({@link shareMerges}); one it holds
   * refused is kept as the book has it; both counted held. Another is staged as a new book's (24c's, nothing pushed).
   */
  protected override share(piece: SharePiece): ImportOp[] {
    const { holdings, bookId } = this.#update;
    if (!holdings.shares.has(piece.id)) return this.#keeps(holdings.refusedShares, piece.id) ? [] : super.share(piece);
    this.#held += 1;
    this.#shares.push(shareRecordOf(piece, bookId));
    return [];
  }

  /** F7: a refused book record gives nothing; any other refused record as 24c's P12–P21. */
  protected override refused(piece: RefusedPiece, found: Uint8Array[]): ImportOp[] {
    return piece.store === 'books' ? [] : super.refused(piece, found);
  }

  /** Principle 3: whether the book holds `id` among `ids`; such a record of the bundle is kept as the book has it, and counted. */
  #keeps(ids: ReadonlySet<string>, id: string): boolean {
    if (!ids.has(id)) return false;
    this.#held += 1;
    return true;
  }
}
